import { Avatar, Box, Skeleton } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import Iconify from 'src/components/iconify';
import Image from 'src/components/image';
import Label from 'src/components/label';
import { InvitationDetails } from './useInvitationDetails';

const AVATAR_SIZE = { xs: 72, md: 96 };

/** Clinic photo (or a navy band when there is none) with the logo overlapping its edge. */
export default function InvitationClinicCover({
  details,
}: {
  details: InvitationDetails;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <Box sx={{ position: 'relative', mb: { xs: 5, md: 6 } }}>
      <Box
        sx={(theme) => ({
          position: 'relative',
          height: { xs: 176, md: 280 },
          overflow: 'hidden',
          // The navy band stands in for a missing photo only; while loading or
          // behind a photo it would show through the rounded corners.
          ...(details.isLoading || details.photo
            ? { bgcolor: 'background.neutral' }
            : {
                bgcolor: 'text.primary',
                backgroundImage: `radial-gradient(circle at 85% 0%, ${alpha(
                  theme.palette.primary.main,
                  0.45,
                )}, transparent 55%), radial-gradient(circle at 10% 120%, ${alpha(
                  theme.palette.secondary.main,
                  0.35,
                )}, transparent 50%)`,
              }),
        })}
      >
        {details.isLoading ? (
          <Skeleton variant="rectangular" sx={{ height: 1, borderRadius: 0 }} />
        ) : (
          details.photo && (
            <Image
              src={details.photo}
              alt={details.name}
              sx={{ position: 'absolute', inset: 0, height: 1 }}
              imageSx={{ objectFit: 'cover', objectPosition: 'center top' }}
            />
          )
        )}
        <Label
          variant="filled"
          startIcon={<Iconify icon="solar:letter-bold-duotone" />}
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            textTransform: 'none',
            bgcolor: 'background.default',
            color: 'text.primary',
          }}
        >
          {t('invitationAcceptance.eyebrow')}
        </Label>
      </Box>
      <Avatar
        src={details.logoUrl}
        alt={details.name}
        variant="rounded"
        sx={(theme) => ({
          position: 'absolute',
          left: { xs: 16, md: 24 },
          bottom: { xs: -36, md: -48 },
          width: AVATAR_SIZE,
          height: AVATAR_SIZE,
          borderRadius: 3,
          border: `4px solid ${theme.palette.background.paper}`,
          bgcolor: 'primary.lighter',
          color: 'text.primary',
          typography: 'h4',
          boxShadow: theme.customShadows.z8,
        })}
      >
        {details.initials}
      </Avatar>
    </Box>
  );
}
